import type { OperatorTenant } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, FlaskConical, Plus, Search, SearchX } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { formatNumber, formatUsd } from "@/components/charts";
import { EmptyState } from "@/components/EmptyState";
import { FilterChips } from "@/components/FilterChips";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { formatDate } from "../i18n/format.ts";
import { consoleTenantPath, Link, navigate } from "../router.tsx";
import { useCsrfHeaders } from "./operator.tsx";
import {
  consoleRefusal,
  limitText,
  SpendAlert,
  spendLevel,
  spendText,
  TenantStatus,
  UsageMeter,
} from "./parts.tsx";
import { consoleKeys, usePlans, useTenants } from "./queries.ts";

type TenantFilter = "all" | "active" | "suspended" | "deletion" | "synthetic";
const FILTER_KEYS: readonly TenantFilter[] = [
  "all",
  "active",
  "suspended",
  "deletion",
  "synthetic",
];
const FILTERS: Record<TenantFilter, (tenant: OperatorTenant) => boolean> = {
  all: () => true,
  active: (tenant) => tenant.status === "active",
  suspended: (tenant) => tenant.status === "suspended",
  deletion: (tenant) => tenant.status === "deletion_scheduled",
  synthetic: (tenant) => !!tenant.modelSpend?.syntheticUsd,
};

/**
 * Every tenant of the instance: status, plan, active users, this month's OCR use and spend,
 * searchable by name and filtered by status (spec "Tenant list search and filters"). A table on
 * a wide container, rows below it: no sideways scroll on a phone.
 */
export function TenantsPage() {
  const { t } = useTranslation();
  const tenants = useTenants();
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<TenantFilter>("all");
  const all = tenants.data ?? [];
  const needle = query.trim().toLocaleLowerCase();
  const searched = all.filter(
    (tenant) => !needle || tenant.name.toLocaleLowerCase().includes(needle),
  );
  const list = searched.filter(FILTERS[filter]);
  return (
    <Page width="wide">
      <PageHeader
        title={t("console.tenants.title")}
        lead={t("console.tenants.lead")}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("console.tenants.create")}
          </Button>
        }
      />
      {tenants.isError ? (
        <Notice tone="danger">{tenants.error.message}</Notice>
      ) : !tenants.data ? (
        <Skeleton className="h-48 w-full" />
      ) : all.length === 0 ? (
        <EmptyState icon={Building2} title={t("console.tenants.none")}>
          {t("console.tenants.noneHint")}
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
                strokeWidth={1.75}
              />
              <Input
                type="search"
                aria-label={t("console.tenantList.search")}
                placeholder={t("console.tenantList.search")}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
                className="h-8 pl-9"
              />
            </div>
            <FilterChips
              label={t("console.tenantList.filters")}
              value={filter}
              onChange={setFilter}
              // A chip only when it matches a tenant (All, and the pressed chip, always: a
              // refetch must not hide the filter in use). Counts follow the search.
              options={FILTER_KEYS.filter(
                (key) => key === "all" || key === filter || all.some(FILTERS[key]),
              ).map((key) => ({
                value: key,
                label: t(`console.tenantList.${key}`),
                count: searched.filter(FILTERS[key]).length,
              }))}
            />
          </div>
          {list.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title={t("console.tenantList.noMatch")}
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setQuery("");
                    setFilter("all");
                  }}
                >
                  {t("console.tenantList.showAll")}
                </Button>
              }
            >
              {t("console.tenantList.noMatchHint")}
            </EmptyState>
          ) : (
            <div className="@container">
              <TenantRows tenants={list} />
              <TenantTable tenants={list} />
            </div>
          )}
        </div>
      )}
      {creating ? <CreateTenantDialog onClose={() => setCreating(false)} /> : null}
    </Page>
  );
}

/**
 * The tenant's name and, when its spend includes synthetic demo spend, one mark under it. The
 * amount for screen readers goes with it, except in the table, whose row header names every
 * cell: there it sits in the spend cell (`SyntheticDetail`).
 */
function TenantName({ tenant, detail = true }: { tenant: OperatorTenant; detail?: boolean }) {
  const { t } = useTranslation();
  const synthetic = tenant.modelSpend?.syntheticUsd;
  return (
    <>
      <Link
        href={consoleTenantPath(tenant.id)}
        className="font-medium text-fg underline-offset-4 hover:underline"
      >
        {tenant.name}
      </Link>
      {synthetic ? (
        <span className="mt-0.5 flex items-center gap-1 text-xs font-normal text-fg-muted">
          <FlaskConical aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
          <span aria-hidden={detail}>{t("console.tenantList.syntheticMark")}</span>
          {detail ? <SyntheticDetail tenant={tenant} /> : null}
        </span>
      ) : null}
    </>
  );
}

function SyntheticDetail({ tenant }: { tenant: OperatorTenant }) {
  const { t } = useTranslation();
  const synthetic = tenant.modelSpend?.syntheticUsd;
  return synthetic ? (
    <span className="sr-only">
      {t("console.syntheticSpendDetail", { amount: formatUsd(synthetic) })}
    </span>
  ) : null;
}

function OcrUse({ tenant, className }: { tenant: OperatorTenant; className?: string }) {
  const { t } = useTranslation();
  return (
    <>
      {`${formatNumber(tenant.ocrBudget.usedThisMonth)} / ${limitText(tenant.ocrBudget.monthlyPages)}`}
      <UsageMeter
        used={tenant.ocrBudget.usedThisMonth}
        limit={tenant.ocrBudget.monthlyPages}
        label={t("console.tenants.ocr")}
        className={className}
      />
    </>
  );
}

function SpendUse({ tenant, className }: { tenant: OperatorTenant; className?: string }) {
  const { t } = useTranslation();
  return (
    <>
      {spendText(tenant.modelSpend)}
      {tenant.modelSpend ? (
        <UsageMeter
          used={tenant.modelSpend.usedThisMonth}
          limit={tenant.modelSpend.monthlyUsd}
          label={t("console.tenants.spend")}
          level={spendLevel(tenant.modelSpend)}
          className={className}
        />
      ) : null}
      <span className="block">
        <SpendAlert spend={tenant.modelSpend} />
      </span>
    </>
  );
}

function PurgeLine({ tenant }: { tenant: OperatorTenant }) {
  const { t } = useTranslation();
  return tenant.deletionDueAt ? (
    <span className="block text-xs text-fg-muted">
      {t("console.tenants.purgeOn", { date: formatDate(tenant.deletionDueAt) })}
    </span>
  ) : null;
}

/** From a wide container: one row per tenant, seven columns. */
function TenantTable({ tenants }: { tenants: OperatorTenant[] }) {
  const { t } = useTranslation();
  return (
    <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface-raised @3xl:block">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">{t("console.tenants.caption")}</caption>
        <thead className="border-b border-line text-xs text-fg-muted">
          <tr>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("console.tenants.name")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("console.tenants.status")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("console.tenants.plan")}
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              {t("console.tenants.users")}
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              {t("console.tenants.ocr")}
            </th>
            <th scope="col" className="px-4 py-2 text-right font-medium">
              {t("console.tenants.spend")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("console.tenants.created")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {tenants.map((tenant) => (
            <tr key={tenant.id} className="align-top">
              <th scope="row" className="px-4 py-2.5 text-left font-medium">
                <TenantName tenant={tenant} detail={false} />
              </th>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <TenantStatus status={tenant.status} />
                <PurgeLine tenant={tenant} />
              </td>
              <td className="px-4 py-2.5 text-fg-secondary">{tenant.plan.name}</td>
              <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">
                {formatNumber(tenant.userCount)}
              </td>
              <td className="px-4 py-2.5 text-right whitespace-nowrap text-fg-secondary tabular-nums">
                <OcrUse tenant={tenant} className="ml-auto w-20" />
              </td>
              <td className="px-4 py-2.5 text-right whitespace-nowrap text-fg-secondary tabular-nums">
                <SpendUse tenant={tenant} className="ml-auto w-24" />
                <SyntheticDetail tenant={tenant} />
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap text-fg-secondary tabular-nums">
                {formatDate(tenant.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Below a wide container, the tenants as rows: the name and its mark, a status · plan · users
 * line, then this month's OCR pages and spend side by side, each with its meter. Nothing is
 * truncated: every figure of the table is here, worded.
 */
function TenantRows({ tenants }: { tenants: OperatorTenant[] }) {
  const { t } = useTranslation();
  return (
    <ul
      aria-label={t("console.tenants.caption")}
      className="divide-y divide-line rounded-lg border border-line bg-surface-raised @3xl:hidden"
    >
      {tenants.map((tenant) => (
        <li key={tenant.id} className="flex flex-col gap-2 px-4 py-3 text-sm">
          <span className="flex flex-col">
            <TenantName tenant={tenant} />
          </span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-fg-secondary">
            <TenantStatus status={tenant.status} />
            <span aria-hidden="true">·</span>
            <span>{tenant.plan.name}</span>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">
              {t("console.tenantList.usersCount", { count: tenant.userCount })}
            </span>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">
              {t("console.tenants.createdOn", { date: formatDate(tenant.createdAt) })}
            </span>
          </span>
          <PurgeLine tenant={tenant} />
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs @sm:grid-cols-2">
            <div className="flex min-w-0 flex-col">
              <dt className="text-fg-muted">{t("console.tenants.ocr")}</dt>
              <dd className="text-fg-secondary tabular-nums">
                <OcrUse tenant={tenant} className="max-w-48" />
              </dd>
            </div>
            <div className="flex min-w-0 flex-col">
              <dt className="text-fg-muted">{t("console.tenants.spend")}</dt>
              <dd className="text-fg-secondary tabular-nums">
                <SpendUse tenant={tenant} className="max-w-48" />
              </dd>
            </div>
          </dl>
        </li>
      ))}
    </ul>
  );
}

/** A managed tenant, its root entity and first tenant admin (invited by e-mail). */
function CreateTenantDialog(props: { onClose: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const plans = usePlans();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [planId, setPlanId] = useState("");
  const create = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.operator.tenants.$post(
          {
            json: {
              name: name.trim(),
              adminEmail: adminEmail.trim(),
              ...(adminName.trim() ? { adminName: adminName.trim() } : {}),
              ...(planId ? { planId } : {}),
            },
          },
          headers,
        ),
      ),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: consoleKeys.tenants });
      props.onClose();
      navigate(consoleTenantPath(created.tenant.id));
    },
  });
  // The e-mail is another organisation's: said under the field (spec "An admin e-mail belongs
  // to one tenant"), never which organisation.
  const emailInUse = create.error instanceof ApiError && create.error.code === "admin_email_in_use";
  return (
    <AppDialog
      open
      title={t("console.tenants.createTitle")}
      description={t("console.tenants.createBody")}
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && adminEmail.trim() && !create.isPending) {
            create.mutate();
          }
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-name`}>{t("console.tenants.name")}</Label>
          <Input
            id={`${id}-name`}
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-email`}>{t("console.tenants.adminEmail")}</Label>
          <Input
            id={`${id}-email`}
            type="email"
            required
            autoComplete="off"
            value={adminEmail}
            aria-invalid={emailInUse || undefined}
            aria-describedby={emailInUse ? `${id}-email-error` : undefined}
            onChange={(e) => setAdminEmail(e.currentTarget.value)}
          />
          {emailInUse ? (
            <span id={`${id}-email-error`}>
              <InlineError>{t("console.tenants.emailInUse")}</InlineError>
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-admin`}>{t("console.tenants.adminName")}</Label>
          <Input
            id={`${id}-admin`}
            maxLength={200}
            value={adminName}
            onChange={(e) => setAdminName(e.currentTarget.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-plan`}>{t("console.tenants.plan")}</Label>
          <NativeSelect
            id={`${id}-plan`}
            value={planId}
            onChange={(e) => setPlanId(e.currentTarget.value)}
          >
            <option value="">{t("console.tenants.defaultPlan")}</option>
            {(plans.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        {create.error && !emailInUse ? (
          <Notice tone="danger">{consoleRefusal(create.error)}</Notice>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={!name.trim() || !adminEmail.trim() || create.isPending}>
            {t("console.tenants.createConfirm")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}
